import React from 'react';
import { AlertCircle, Home, RefreshCw, ChevronDown, ChevronUp, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import config from '../config';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      reportSending: false,
      reportSent: false,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo,
    });
    console.error(`[${config.APP_NAME} v${config.APP_VERSION}] Error caught by ErrorBoundary:`, error);
    console.error('Component stack:', errorInfo?.componentStack);
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      reportSending: false,
      reportSent: false,
    });
  };

  handleReportError = async () => {
    this.setState({ reportSending: true });
    try {
      const token = localStorage.getItem('token');
      const endpoint = token
        ? `${config.API_BASE_URL}/api/system/improvements/error-reports`
        : `${config.API_BASE_URL}/api/system/improvements/error-reports/public`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          error_message: this.state.error?.toString() || 'Unknown error',
          error_stack: this.state.error?.stack || null,
          component_stack: this.state.errorInfo?.componentStack || null,
          page_url: window.location.href,
          browser_info: navigator.userAgent,
          app_version: config.APP_VERSION,
        }),
      });

      if (response.ok) {
        this.setState({ reportSent: true });
        toast.success('Error report sent successfully. The development team will investigate.');
      } else {
        toast.error('Failed to send error report. Please try again.');
      }
    } catch (err) {
      console.error('Failed to send error report:', err);
      toast.error('Could not send error report. Please check your connection.');
    } finally {
      this.setState({ reportSending: false });
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
          <div className="w-full max-w-lg">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-r from-red-500 to-red-600 px-8 pt-8 pb-6">
                <div className="flex items-center gap-3 mb-4">
                  <img src="/logo.png" alt="Office Suite" className="w-10 h-10 rounded-lg object-contain" />
                  <div>
                    <span className="font-bold text-white text-lg">Office Suite</span>
                    <p className="text-red-100 text-xs">ERP System</p>
                  </div>
                </div>
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                  </div>
                </div>
                <h1 className="text-2xl font-bold text-white text-center mb-1">
                  Something went wrong
                </h1>
                <p className="text-red-100 text-center text-sm">
                  {config.APP_NAME} v{config.APP_VERSION}
                </p>
              </div>

              <div className="p-8 pt-6">
                <p className="text-slate-600 text-center mb-6">
                  We encountered an unexpected error. You can try again, return to the dashboard, or report this issue.
                </p>

                {this.state.error && (
                  <div className="mb-6">
                    <button
                      onClick={() => this.setState(s => ({ showDetails: !s.showDetails }))}
                      className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mx-auto mb-2 px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      {this.state.showDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      {this.state.showDetails ? 'Hide' : 'Show'} error details
                    </button>
                    {this.state.showDetails && (
                      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 max-h-48 overflow-y-auto">
                        <p className="text-xs font-mono text-red-600 break-words font-medium">
                          {this.state.error.toString()}
                        </p>
                        {this.state.errorInfo?.componentStack && (
                          <details className="mt-3">
                            <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700 font-medium">
                              Component Stack Trace
                            </summary>
                            <pre className="text-[11px] font-mono text-slate-500 mt-2 whitespace-pre-wrap leading-relaxed">
                              {this.state.errorInfo.componentStack}
                            </pre>
                          </details>
                        )}
                        {this.state.error.stack && (
                          <details className="mt-3">
                            <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700 font-medium">
                              Full Stack Trace
                            </summary>
                            <pre className="text-[11px] font-mono text-slate-500 mt-2 whitespace-pre-wrap leading-relaxed">
                              {this.state.error.stack}
                            </pre>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex flex-col gap-3">
                  <button
                    onClick={this.resetError}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2 shadow-sm"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Try Again</span>
                  </button>

                  <a
                    href="/"
                    className="w-full px-6 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                  >
                    <Home className="w-4 h-4" />
                    <span>Go to Dashboard</span>
                  </a>

                  <button
                    onClick={this.handleReportError}
                    disabled={this.state.reportSending || this.state.reportSent}
                    className={`w-full px-4 py-2.5 font-medium rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 text-sm ${
                      this.state.reportSent
                        ? 'bg-emerald-50 text-emerald-600 cursor-default'
                        : this.state.reportSending
                        ? 'text-slate-400 cursor-wait'
                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {this.state.reportSending ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Sending report...</span>
                      </>
                    ) : this.state.reportSent ? (
                      <>
                        <Send className="w-3.5 h-3.5" />
                        <span>Report sent - Thank you!</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-3.5 h-3.5" />
                        <span>Report this error</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
